import { useState, useRef, useEffect, useMemo } from "react";
import { X, Link2, ArrowUpRight, ArrowRight, Pencil, Globe, Lock, Folder, Trash2, Check, ChevronDown, StickyNote, Layers, RefreshCw } from "lucide-react";
import { ResponsiveSheet } from "./ResponsiveSheet";
import type { Thought } from "./ThoughtCard";
import { useBurrow, type Thread } from "./BurrowStore";
import { useResurface } from "../lib/useResurface";
import { CollectionPicker } from "./CollectionPicker";
import { ThreadPicker } from "./ThreadPicker";
import { ThoughtBody } from "./ThoughtBody";
import { KnowledgeChainSidebar } from "./KnowledgeChainSidebar";
import { loadKnowledgeChain } from "../lib/social/socialApi";
import type { KnowledgeChainLink } from "../types/social";
import { useSignedObjectUrl } from "../lib/useSignedObjectUrl";
import { supabase } from "../lib/supabaseClient";
import { useNavigate } from "react-router";
import { ConfirmDialog } from "./ConfirmDialog";

// Source links are stored as the user typed them; prepend https:// for the
// href when no scheme is present so the link still resolves.
const normalizeSourceUrl = (u: string) =>
  /^https?:\/\//i.test(u) ? u : `https://${u}`;

interface CardDetailSheetProps {
  thought: Thought | null;
  onClose: () => void;
}

export function CardDetailSheet({ thought, onClose }: CardDetailSheetProps) {
  const { state, updateThought, deleteThought, withdrawFromCommons, addTag, removeTag, getThoughtCollections, removeFromCollection, setActiveThread, showToast } = useBurrow();
  const { resurfacedIds, setResurface } = useResurface();
  const navigate = useNavigate();
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingBody, setEditingBody] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [bodyDraft, setBodyDraft] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [showTagInput, setShowTagInput] = useState(false);
  const [showCollectionPicker, setShowCollectionPicker] = useState(false);
  const [showThreadPicker, setShowThreadPicker] = useState(false);
  const [notesOpen, setNotesOpen] = useState(true);
  const [notesDraft, setNotesDraft] = useState("");
  const [notesEditing, setNotesEditing] = useState(false);
  const [sourceEditing, setSourceEditing] = useState(false);
  const [sourceUrlDraft, setSourceUrlDraft] = useState("");
  const [sourceTitleDraft, setSourceTitleDraft] = useState("");
  const titleRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const tagRef = useRef<HTMLInputElement>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (editingTitle && titleRef.current) titleRef.current.focus();
  }, [editingTitle]);

  useEffect(() => {
    if (editingBody && bodyRef.current) bodyRef.current.focus();
  }, [editingBody]);

  useEffect(() => {
    if (showTagInput && tagRef.current) tagRef.current.focus();
  }, [showTagInput]);

  // Reset editing state when thought changes
  useEffect(() => {
    setEditingTitle(false);
    setEditingBody(false);
    setShowTagInput(false);
    setTagInput("");
    setNotesDraft(thought?.notes ?? "");
    setNotesEditing(false);
    setSourceEditing(false);
  }, [thought?.id]);

  // Keep notes draft in sync when notes update externally (e.g. perspective saves)
  useEffect(() => {
    if (!notesEditing) setNotesDraft(thought?.notes ?? "");
  }, [thought?.notes]);

  const handleSaveTitle = () => {
    if (thought) {
      updateThought(thought.id, { title: titleDraft.trim() || undefined });
    }
    setEditingTitle(false);
  };

  const handleSaveBody = () => {
    if (thought && bodyDraft.trim()) {
      updateThought(thought.id, { body: bodyDraft.trim() });
    }
    setEditingBody(false);
  };

  const openEditSource = () => {
    setSourceUrlDraft(thought?.source_url ?? "");
    setSourceTitleDraft(thought?.source_title ?? "");
    setSourceEditing(true);
  };

  const handleSaveSource = () => {
    if (!thought) return;
    const url = sourceUrlDraft.trim();
    const title = sourceTitleDraft.trim();
    const normalized = url && !/^https?:\/\//i.test(url) ? `https://${url}` : url;
    updateThought(thought.id, {
      source_url: normalized || undefined,
      source_title: title || undefined,
    });
    setSourceEditing(false);
  };

  const handleClearSource = () => {
    if (!thought) return;
    updateThought(thought.id, { source_url: undefined, source_title: undefined });
    setSourceUrlDraft("");
    setSourceTitleDraft("");
    setSourceEditing(false);
  };

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (thought && tag) {
      addTag(thought.id, tag);
    }
    setTagInput("");
    setShowTagInput(false);
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);
  // Removing a card from a PUBLIC collection can anonymize it — gate with consent.
  const [confirmRemoveColId, setConfirmRemoveColId] = useState<string | null>(null);

  const handleDelete = () => {
    if (thought) {
      deleteThought(thought.id);
      onClose();
    }
  };

  const collections = thought ? getThoughtCollections(thought.id) : [];
  const safeLabels = thought?.labels && Array.isArray(thought.labels) ? thought.labels : [];

  // Load connected thoughts with connection notes
  const [connectedThoughts, setConnectedThoughts] = useState<{ id: string; title?: string; body: string; collectionId?: string; note?: string }[]>([]);
  useEffect(() => {
    if (!thought) { setConnectedThoughts([]); return; }
    (async () => {
      const { data } = await supabase
        .from("connections")
        .select("from_thought_id,to_thought_id,note")
        .or(`from_thought_id.eq.${thought.id},to_thought_id.eq.${thought.id}`);
      if (data) {
        const connected = data
          .map((c) => {
            const otherId = c.from_thought_id === thought.id ? c.to_thought_id : c.from_thought_id;
            const t = state.thoughts.find((th) => th.id === otherId);
            if (!t) return null;
            const col = state.collections.find((col) => col.card_ids.includes(otherId));
            return { id: t.id, title: t.title, body: t.body, collectionId: col?.id, note: c.note ?? undefined };
          })
          .filter(Boolean) as { id: string; title?: string; body: string; collectionId?: string; note?: string }[];
        setConnectedThoughts(connected);
      }
    })();
  }, [thought?.id, state.thoughts, state.collections]);

  // Load threads this thought belongs to. Always query (rather than gating on
  // thought.in_thread) so a thread added from the picker shows up immediately.
  const [thoughtThreads, setThoughtThreads] = useState<Thread[]>([]);
  useEffect(() => {
    if (!thought?.id) { setThoughtThreads([]); return; }
    (async () => {
      const { data } = await supabase
        .from("thread_thoughts")
        .select("thread_id")
        .eq("thought_id", thought.id);
      if (data && data.length > 0) {
        const threadIds = data.map((r) => r.thread_id);
        const matches = state.savedThreads.filter((t) => threadIds.includes(t.id));
        setThoughtThreads(matches);
      } else {
        setThoughtThreads([]);
      }
    })();
  }, [thought?.id, state.savedThreads]);

  // Provenance for borrowed cards. A borrow only left `source_card_id` /
  // `source_edition_id` on the row — nothing on this sheet ever surfaced them,
  // so a borrowed card looked identical to one you wrote. The chain RPC is
  // SECURITY DEFINER, so it walks back past cards you can't read directly, and
  // it resolves each step's public destination (the edition it was borrowed out
  // of) for us.
  const isBorrowed = Boolean(thought?.source_card_id);
  const [chain, setChain] = useState<KnowledgeChainLink[]>([]);
  const [chainLoading, setChainLoading] = useState(false);
  useEffect(() => {
    if (!thought?.id || !isBorrowed) { setChain([]); setChainLoading(false); return; }
    let cancelled = false;
    setChainLoading(true);
    (async () => {
      const links = await loadKnowledgeChain(thought.id);
      if (cancelled) return;
      setChain(links);
      setChainLoading(false);
    })();
    return () => { cancelled = true; };
  }, [thought?.id, isBorrowed]);

  // All unique labels across the whole burrow for autocomplete
  const allLabels = useMemo(() => {
    const counts = new Map<string, number>();
    state.thoughts.forEach((t) => {
      (t.labels ?? []).forEach((l) => counts.set(l, (counts.get(l) ?? 0) + 1));
    });
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([label]) => label);
  }, [state.thoughts]);

  const filteredSuggestions = tagInput.trim()
    ? allLabels.filter((l) => l.includes(tagInput.trim().toLowerCase()) && !safeLabels.includes(l)).slice(0, 6)
    : allLabels.filter((l) => !safeLabels.includes(l)).slice(0, 6);
  const safeCollections = Array.isArray(collections) ? collections : [];
  const signed = useSignedObjectUrl({ bucket: "thought-images", path: thought?.image_path });
  const imageUrl = signed.status === "ready" ? signed.url : thought?.image_url;

  return (
    <>
      <ResponsiveSheet
        open={!!thought}
        onClose={onClose}
        label="Thought details"
        desktop="right"
        desktopWidth="max-w-[520px]"
        mobileHeight="tall"
        // Above the top nav (z-50) so the header row (date, trash, close) isn't
        // hidden behind it; still below the pickers (z-60) this sheet opens.
        zIndex={55}
      >
        {thought && (
          <>
              <div className="p-5 md:p-8">
                {/* Header */}
                <div className="flex items-start justify-between gap-3 mb-6">
                  <div className="flex items-center flex-wrap gap-2 min-w-0 text-muted-foreground" style={{ fontSize: "0.78rem" }}>
                    <span>
                      {new Date(thought.created_at).toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                    <span className="text-border">|</span>
                    {/* Visibility is DERIVED from edition membership, not a per-card toggle.
                        A card is public iff it's in at least one public edition. Password-protected
                        editions don't count — cards in them stay private. To make a card public,
                        the owner publishes a collection containing it as a public edition. */}
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${
                        thought.visibility === "public"
                          ? "bg-accent/15 text-accent"
                          : "text-muted-foreground"
                      }`}
                      title={
                        thought.visibility === "public"
                          ? "Public — this card is in at least one public edition."
                          : "Private — publish in a public edition to make this card public."
                      }
                      style={{ fontSize: "0.74rem" }}
                    >
                      {thought.visibility === "public" ? <Globe size={11} /> : <Lock size={11} />}
                      {thought.visibility === "public" ? "Public" : "Private"}
                    </span>
                    {thought.visibility === "public" && (
                      <button
                        onClick={() => setShowWithdrawConfirm(true)}
                        className="text-muted-foreground/50 hover:text-accent underline-offset-2 hover:underline transition-colors"
                        style={{ fontSize: "0.72rem" }}
                        title="Take this card out of the community library and back to a private card"
                      >
                        Withdraw from commons
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="p-2 rounded-xl text-muted-foreground/50 hover:text-destructive hover:bg-destructive/8 transition-all"
                      title="Delete thought"
                    >
                      <Trash2 size={16} />
                    </button>
                    <button
                      onClick={onClose}
                      className="p-2 -mr-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>

                {/* Image */}
                {imageUrl && (
                  <div className="mb-6 rounded-xl overflow-hidden">
                    <img
                      src={imageUrl}
                      alt=""
                      className="w-full object-cover"
                      style={{ maxHeight: "280px" }}
                    />
                  </div>
                )}

                {/* Title — editable */}
                {editingTitle ? (
                  <div className="mb-4">
                    <input
                      ref={titleRef}
                      value={titleDraft}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      onBlur={handleSaveTitle}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveTitle();
                        if (e.key === "Escape") setEditingTitle(false);
                      }}
                      className="w-full bg-transparent border-b border-accent/40 outline-none text-primary pb-1"
                      style={{
                        fontFamily: "'Fraunces', Georgia, serif",
                        fontSize: "1.4rem",
                        lineHeight: "1.35",
                        fontWeight: 400,
                      }}
                      placeholder="Add a title..."
                    />
                  </div>
                ) : thought.title ? (
                  <h2
                    className="text-primary mb-4 group cursor-text"
                    style={{
                      fontFamily: "'Fraunces', Georgia, serif",
                      fontSize: "1.4rem",
                      lineHeight: "1.35",
                      fontWeight: 400,
                    }}
                    onClick={() => {
                      setTitleDraft(thought.title || "");
                      setEditingTitle(true);
                    }}
                  >
                    {thought.title}
                    <Pencil
                      size={14}
                      className="inline-block ml-2 opacity-0 group-hover:opacity-40 transition-opacity text-muted-foreground"
                    />
                  </h2>
                ) : (
                  // No stored title — show a muted "Untitled" placeholder styled
                  // like the title slot. Clicking edits; leaving it empty keeps
                  // the title unset (handleSaveTitle stores `|| undefined`), so
                  // "Untitled" is never persisted or shown on other surfaces.
                  <button
                    onClick={() => {
                      setTitleDraft("");
                      setEditingTitle(true);
                    }}
                    className="group text-muted-foreground/40 hover:text-muted-foreground/70 mb-4 flex items-center gap-2 text-left transition-colors"
                    style={{
                      fontFamily: "'Fraunces', Georgia, serif",
                      fontSize: "1.4rem",
                      lineHeight: "1.35",
                      fontWeight: 400,
                      fontStyle: "italic",
                    }}
                    title="Add a title"
                  >
                    Untitled
                    <Pencil
                      size={14}
                      className="opacity-0 group-hover:opacity-40 transition-opacity"
                    />
                  </button>
                )}

                {/* Body — editable */}
                {editingBody ? (
                  <div className="mb-8">
                    <textarea
                      ref={bodyRef}
                      value={bodyDraft}
                      onChange={(e) => setBodyDraft(e.target.value)}
                      onBlur={handleSaveBody}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") setEditingBody(false);
                      }}
                      rows={6}
                      className="w-full bg-transparent border border-accent/20 rounded-xl outline-none text-foreground/85 p-3 resize-none focus:border-accent/40"
                      style={{ fontSize: "0.95rem", lineHeight: "1.75" }}
                    />
                  </div>
                ) : (
                  <div
                    className="text-foreground/85 mb-8 cursor-text group relative"
                    style={{ fontSize: "0.95rem", lineHeight: "1.75" }}
                    onClick={() => {
                      setBodyDraft(thought.body);
                      setEditingBody(true);
                    }}
                  >
                    <ThoughtBody text={thought.body} />
                    <Pencil
                      size={12}
                      className="absolute top-0 right-0 opacity-0 group-hover:opacity-30 transition-opacity text-muted-foreground"
                    />
                  </div>
                )}

                {/* Divider */}
                <div className="border-t border-border/30 pt-6 space-y-5">
                  {/* Source — add / edit */}
                  <div>
                    <label
                      className="text-muted-foreground/60 block mb-1.5"
                      style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em" }}
                    >
                      Source
                    </label>
                    {sourceEditing ? (
                      <div className="space-y-2">
                        <input
                          value={sourceUrlDraft}
                          onChange={(e) => setSourceUrlDraft(e.target.value)}
                          placeholder="https://…  (link, optional)"
                          autoFocus
                          className="w-full bg-background border border-border/40 rounded-xl px-3 py-2 outline-none text-foreground placeholder:text-muted-foreground/40 focus:border-accent/40 transition-colors"
                          style={{ fontSize: "0.86rem" }}
                          onKeyDown={(e) => { if (e.key === "Escape") setSourceEditing(false); }}
                        />
                        <input
                          value={sourceTitleDraft}
                          onChange={(e) => setSourceTitleDraft(e.target.value)}
                          placeholder="Label — article title or author (optional)"
                          className="w-full bg-background border border-border/40 rounded-xl px-3 py-2 outline-none text-foreground placeholder:text-muted-foreground/40 focus:border-accent/40 transition-colors"
                          style={{ fontSize: "0.86rem" }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveSource();
                            if (e.key === "Escape") setSourceEditing(false);
                          }}
                        />
                        <div className="flex items-center gap-2">
                          <button
                            onClick={handleSaveSource}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent text-accent-foreground hover:opacity-90 transition-all"
                            style={{ fontSize: "0.8rem" }}
                          >
                            <Check size={13} /> Save
                          </button>
                          <button
                            onClick={() => setSourceEditing(false)}
                            className="px-3 py-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all"
                            style={{ fontSize: "0.8rem" }}
                          >
                            Cancel
                          </button>
                          {(thought.source_url || thought.source_title) && (
                            <button
                              onClick={handleClearSource}
                              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-muted-foreground/50 hover:text-destructive hover:bg-destructive/8 transition-all"
                              style={{ fontSize: "0.8rem" }}
                            >
                              <Trash2 size={13} /> Remove
                            </button>
                          )}
                        </div>
                      </div>
                    ) : thought.source_url || thought.source_title ? (
                      <div className="flex items-center gap-2 group/src">
                        {thought.source_url ? (
                          <a
                            href={normalizeSourceUrl(thought.source_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-accent hover:underline flex-1 min-w-0"
                            style={{ fontSize: "0.88rem" }}
                          >
                            <ArrowUpRight size={14} className="shrink-0" />
                            <span className="truncate">{thought.source_title || thought.source_url}</span>
                          </a>
                        ) : (
                          <span className="text-foreground/70 flex-1 min-w-0 truncate" style={{ fontSize: "0.88rem" }}>
                            {thought.source_title}
                          </span>
                        )}
                        <button
                          onClick={openEditSource}
                          className="shrink-0 p-1.5 rounded-lg text-muted-foreground/40 hover:text-accent hover:bg-accent/8 transition-all"
                          title="Edit source"
                        >
                          <Pencil size={12} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={openEditSource}
                        className="text-muted-foreground/50 hover:text-accent transition-colors flex items-center gap-1.5"
                        style={{ fontSize: "0.85rem" }}
                      >
                        <Link2 size={14} /> Add source...
                      </button>
                    )}
                  </div>

                  {/* Borrowed from — only on cards that came from someone else's
                      library. The chain links back through every author to the
                      original, and each step links to the edition it sat in. */}
                  {isBorrowed && (
                    <div>
                      <label
                        className="text-muted-foreground/60 block mb-1.5"
                        style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em" }}
                      >
                        Borrowed from
                      </label>
                      <KnowledgeChainSidebar chain={chain} loading={chainLoading} />
                    </div>
                  )}

                  {/* Connections */}
                  <div>
                    <label
                      className="text-muted-foreground/60 block mb-1.5"
                      style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em" }}
                    >
                      Connections
                    </label>
                    {connectedThoughts.length > 0 ? (
                      <div className="space-y-1">
                        {connectedThoughts.map((conn) => (
                          <div key={conn.id}>
                            <button
                              onClick={() => {
                                if (conn.collectionId) {
                                  onClose();
                                  navigate(`/collections/${conn.collectionId}?view=map`);
                                }
                              }}
                              className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-accent/8 transition-all group/conn text-left"
                            >
                              <Link2 size={12} className="text-accent/60 shrink-0" />
                              <span className="text-foreground/80 flex-1 truncate" style={{ fontSize: "0.85rem" }}>
                                {conn.title || conn.body.slice(0, 50)}
                              </span>
                              {conn.note && (
                                <StickyNote size={10} className="text-accent/40 shrink-0" />
                              )}
                              {conn.collectionId && (
                                <ArrowRight size={12} className="text-muted-foreground/20 group-hover/conn:text-accent transition-colors shrink-0" />
                              )}
                            </button>
                            {conn.note && (
                              <p
                                className="text-muted-foreground/50 px-2.5 pl-7 pb-1"
                                style={{ fontSize: "0.75rem", fontStyle: "italic", lineHeight: "1.4" }}
                              >
                                {conn.note}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span
                        className="text-muted-foreground/50 flex items-center gap-1.5"
                        style={{ fontSize: "0.85rem" }}
                      >
                        <Link2 size={14} />
                        No connections yet
                      </span>
                    )}
                  </div>

                  {/* Labels — editable */}
                  <div>
                    <label
                      className="text-muted-foreground/60 block mb-1.5"
                      style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em" }}
                    >
                      Labels
                    </label>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {safeLabels.map((tag) => (
                        <span
                          key={tag}
                          className="group/tag px-2.5 py-1 rounded-full bg-accent/10 text-accent flex items-center gap-1"
                          style={{ fontSize: "0.78rem" }}
                        >
                          {tag}
                          <button
                            onClick={() => removeTag(thought.id, tag)}
                            className="opacity-0 group-hover/tag:opacity-100 transition-opacity hover:text-destructive"
                          >
                            <X size={10} />
                          </button>
                        </span>
                      ))}
                      {showTagInput ? (
                        <div className="relative flex items-center gap-1">
                          <input
                            ref={tagRef}
                            value={tagInput}
                            onChange={(e) => { setTagInput(e.target.value); setShowSuggestions(true); }}
                            onFocus={() => setShowSuggestions(true)}
                            onBlur={() => {
                              setTimeout(() => {
                                setShowSuggestions(false);
                                if (tagInput.trim()) handleAddTag();
                                else setShowTagInput(false);
                              }, 150);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { handleAddTag(); setShowSuggestions(false); }
                              if (e.key === "Escape") { setShowTagInput(false); setTagInput(""); setShowSuggestions(false); }
                            }}
                            className="px-2 py-0.5 rounded-full border border-accent/40 bg-transparent text-foreground outline-none w-28"
                            style={{ fontSize: "0.78rem" }}
                            placeholder="label name"
                          />
                          <button
                            onClick={handleAddTag}
                            className="text-accent hover:text-foreground transition-colors"
                          >
                            <Check size={12} />
                          </button>
                          {showSuggestions && filteredSuggestions.length > 0 && (
                            <div className="absolute top-full left-0 mt-1 w-40 bg-card border border-border/50 rounded-xl shadow-lg z-10 py-1 max-h-48 overflow-y-auto">
                              {filteredSuggestions.map((label) => (
                                <button
                                  key={label}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    if (thought) addTag(thought.id, label);
                                    setTagInput("");
                                    setShowTagInput(false);
                                    setShowSuggestions(false);
                                  }}
                                  className="w-full text-left px-3 py-1.5 text-foreground/80 hover:bg-accent/10 hover:text-accent transition-colors"
                                  style={{ fontSize: "0.78rem" }}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowTagInput(true)}
                          className="px-2.5 py-1 rounded-full border border-dashed border-border/50 text-muted-foreground/50 hover:text-accent hover:border-accent/40 transition-all"
                          style={{ fontSize: "0.78rem" }}
                        >
                          + Add label
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Collections */}
                  <div>
                    <label
                      className="text-muted-foreground/60 block mb-1.5"
                      style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em" }}
                    >
                      Collections
                    </label>
                        {safeCollections.length > 0 ? (
                      <div className="space-y-1.5 mb-2">
                        {safeCollections.map((col) => (
                          <div
                            key={col.id}
                            className="flex items-center justify-between group/col px-2.5 py-1.5 rounded-lg hover:bg-accent/8 transition-all"
                          >
                            <button
                              onClick={() => { onClose(); navigate(`/collections/${col.id}`); }}
                              className="flex items-center gap-2 flex-1 min-w-0 text-left"
                            >
                              <div
                                className="w-2.5 h-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: col.cover_color || "var(--accent)" }}
                              />
                              <span className="text-foreground/80 truncate" style={{ fontSize: "0.85rem" }}>
                                {col.name}
                              </span>
                              {col.is_public ? (
                                <Globe size={10} className="text-accent shrink-0" />
                              ) : (
                                <Lock size={10} className="text-muted-foreground/40 shrink-0" />
                              )}
                              <ArrowRight size={12} className="text-muted-foreground/20 group-hover/col:text-accent transition-colors shrink-0 ml-auto" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (col.is_public) setConfirmRemoveColId(col.id);
                                else removeFromCollection(thought.id, col.id);
                              }}
                              className="opacity-0 group-hover/col:opacity-100 text-muted-foreground/40 hover:text-destructive transition-all ml-1 shrink-0"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <button
                      onClick={() => setShowCollectionPicker(true)}
                      className="text-muted-foreground/50 hover:text-accent transition-colors flex items-center gap-1.5"
                      style={{ fontSize: "0.85rem" }}
                    >
                      <Folder size={14} />
                      Add to collection...
                    </button>
                  </div>

                  {/* Resurface — flag this card to reappear in the Thinking
                      Space "It's been a while" deck (loose cards show there
                      automatically; this is how filed cards get back in). */}
                  <div>
                    <label
                      className="text-muted-foreground/60 block mb-1.5"
                      style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em" }}
                    >
                      Resurface
                    </label>
                    {resurfacedIds.has(thought.id) ? (
                      <div className="flex items-center gap-2">
                        <span className="flex items-center gap-1.5 text-accent" style={{ fontSize: "0.85rem" }}>
                          <RefreshCw size={14} />
                          Set to resurface
                        </span>
                        <button
                          onClick={() => { setResurface(thought.id, false); showToast("Removed from resurface."); }}
                          className="ml-auto text-muted-foreground/40 hover:text-destructive transition-colors"
                          style={{ fontSize: "0.8rem" }}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setResurface(thought.id, true); showToast("Will resurface soon."); }}
                        className="text-muted-foreground/50 hover:text-accent transition-colors flex items-center gap-1.5"
                        style={{ fontSize: "0.85rem" }}
                      >
                        <RefreshCw size={14} />
                        Resurface later
                      </button>
                    )}
                  </div>

                  {/* Threads */}
                  <div>
                    <label
                      className="text-muted-foreground/60 block mb-1.5"
                      style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em" }}
                    >
                      Trails
                    </label>
                    {thoughtThreads.length > 0 && (
                      <div className="space-y-1.5 mb-2">
                        {thoughtThreads.map((thread) => (
                          <button
                            key={thread.id}
                            onClick={async () => {
                              const { data } = await supabase
                                .from("thread_thoughts")
                                .select("thought_id,position")
                                .eq("thread_id", thread.id)
                                .order("position", { ascending: true });
                              if (data) {
                                setActiveThread(data.map((r) => r.thought_id));
                                onClose();
                                navigate("/thinking");
                              }
                            }}
                            className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-accent/8 transition-all group/thread"
                          >
                            <Layers size={12} className="text-accent/60 shrink-0" />
                            <span className="text-foreground/80 flex-1 text-left" style={{ fontSize: "0.85rem" }}>
                              {thread.title}
                            </span>
                            <ArrowRight size={12} className="text-muted-foreground/20 group-hover/thread:text-accent transition-colors shrink-0" />
                          </button>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={() => setShowThreadPicker(true)}
                      className="text-muted-foreground/50 hover:text-accent transition-colors flex items-center gap-1.5"
                      style={{ fontSize: "0.85rem" }}
                    >
                      <Layers size={14} />
                      Add to trail...
                    </button>
                  </div>

                  {/* Notes */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label
                        className="text-muted-foreground/60 flex items-center gap-1.5"
                        style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em" }}
                      >
                        <StickyNote size={11} /> Notes
                      </label>
                      <button
                        onClick={() => {
                          if (notesEditing && thought) {
                            updateThought(thought.id, { notes: notesDraft.trim() || undefined });
                          }
                          setNotesEditing(!notesEditing);
                        }}
                        className="text-muted-foreground/40 hover:text-accent transition-colors"
                      >
                        <Pencil size={11} />
                      </button>
                    </div>
                    {notesEditing ? (
                      <textarea
                        value={notesDraft}
                        onChange={(e) => setNotesDraft(e.target.value)}
                        onBlur={() => {
                          if (thought) updateThought(thought.id, { notes: notesDraft.trim() || undefined });
                        }}
                        rows={6}
                        placeholder="Private notes, context, or reminders about this card…"
                        className="w-full rounded-xl border border-border/30 bg-muted/30 p-3 text-foreground/80 outline-none focus:border-accent/30 resize-none placeholder:text-muted-foreground/40"
                        style={{ fontSize: "0.85rem", lineHeight: "1.65" }}
                        autoFocus
                      />
                    ) : notesDraft ? (
                      <div
                        className="rounded-xl bg-muted/20 border border-border/20 p-3 text-foreground/70 cursor-pointer hover:bg-muted/30 transition-all"
                        onClick={() => setNotesEditing(true)}
                        style={{ fontSize: "0.85rem", lineHeight: "1.7" }}
                      >
                        {notesDraft.split("\n").map((line, idx) => {
                          // Render **bold** markdown
                          const boldMatch = line.match(/^\*\*(.+)\*\*$/);
                          if (boldMatch) {
                            return (
                              <p key={idx} className="text-primary font-medium mt-2 first:mt-0" style={{ fontSize: "0.82rem" }}>
                                {boldMatch[1]}
                              </p>
                            );
                          }
                          if (line === "---") {
                            return <hr key={idx} className="border-border/20 my-3" />;
                          }
                          if (!line.trim()) return <br key={idx} />;
                          return (
                            <p key={idx} className="text-foreground/60" style={{ fontSize: "0.82rem" }}>
                              {line}
                            </p>
                          );
                        })}
                      </div>
                    ) : (
                      <button
                        onClick={() => setNotesEditing(true)}
                        className="w-full text-left rounded-xl border border-dashed border-border/30 p-3 text-muted-foreground/40 hover:text-muted-foreground/60 hover:border-border/50 transition-all"
                        style={{ fontSize: "0.82rem" }}
                      >
                        Add a note...
                      </button>
                    )}
                  </div>
                </div>
              </div>
          </>
        )}
      </ResponsiveSheet>

      {/* Collection picker */}
      {thought && (
        <CollectionPicker
          open={showCollectionPicker}
          onClose={() => setShowCollectionPicker(false)}
          thoughtIds={[thought.id]}
        />
      )}

      {/* Thread picker */}
      {thought && (
        <ThreadPicker
          open={showThreadPicker}
          onClose={() => setShowThreadPicker(false)}
          thoughtId={thought.id}
          containingThreadIds={new Set(thoughtThreads.map((t) => t.id))}
          onCloseSheet={onClose}
        />
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete this thought?"
        description={
          thought?.visibility === "public"
            ? "This card is public — deleting removes it from your library AND the community library for good. Copies other people borrowed stay with them. This can't be undone."
            : "This can't be undone."
        }
        onConfirm={() => { setShowDeleteConfirm(false); handleDelete(); }}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {/* Withdraw from commons */}
      <ConfirmDialog
        open={showWithdrawConfirm}
        title="Withdraw this card from the commons?"
        description="It leaves the community library entirely and returns to your library as a fresh private card — labels and notes intact. Copies other people borrowed stay with them, but their link back to you is removed."
        confirmLabel="Withdraw"
        onConfirm={() => {
          setShowWithdrawConfirm(false);
          if (thought) void withdrawFromCommons(thought.id);
        }}
        onCancel={() => setShowWithdrawConfirm(false)}
      />

      {/* Remove from a public collection — can anonymize the card */}
      <ConfirmDialog
        open={confirmRemoveColId !== null}
        title="Remove this card from a public collection?"
        description="If it isn't in another public collection or edition, the card stays in the community library as an anonymous card — unlinked from you, labels hidden — until you re-share or withdraw it."
        confirmLabel="Remove card"
        onConfirm={() => {
          if (thought && confirmRemoveColId) removeFromCollection(thought.id, confirmRemoveColId);
          setConfirmRemoveColId(null);
        }}
        onCancel={() => setConfirmRemoveColId(null)}
      />
    </>
  );
}
